import { X } from "lucide-react";

import { useTranslations } from "@/shared/lang/useTranslations";

import type { EffectiveSummary } from "../../types/access.types";

export interface AccessEffectiveCellProps {
  /** The resolved figure. Only read when neither unknown nor `noRoles`. */
  summary: EffectiveSummary | undefined;
  /**
   * The principal holds no roles — rendered as "—".
   *
   * Passed in rather than inferred from `summary`, because a principal with no roles still HAS a summary (an
   * all-zero one). Inferring it from a missing summary would print `0/N` for them, which reads as an
   * authoritative "resolved to nothing" instead of "nothing assigned".
   */
  noRoles: boolean;
  /** This principal's roles fetch failed: access is unknown, not confirmed as none. */
  rolesFailed: boolean;
  /** The roles catalog 403'd, so no principal's access can be resolved. */
  catalogUnavailable: boolean;
  /**
   * Render the reason the figure is unknown as visible text instead of a `title`.
   *
   * On a touch screen a `title` is unreachable, and this is the one place where that matters: the whole point
   * of the "?" is that it means UNKNOWN rather than "no access", and on a security surface an operator who
   * cannot read that distinction will assume the worse-looking one. Cards pass `true`.
   */
  explainUnknown?: boolean;
}

/**
 * The effective-access figure of one principal — `allowed/total` plus the deny count — with its three
 * degraded states: unknown ("?"), no roles ("—") and resolved.
 *
 * Shared by the users and API-keys lists, in both their table and card faces, because the distinction between
 * "unknown" and "none" is the single most consequential thing these surfaces say, and four copies of it is
 * four chances to say it differently.
 */
export function AccessEffectiveCell({
  summary,
  noRoles,
  rolesFailed,
  catalogUnavailable,
  explainUnknown = false,
}: AccessEffectiveCellProps) {
  const { t } = useTranslations("settings");

  if (rolesFailed || catalogUnavailable) {
    const reason = catalogUnavailable
      ? t("access_effective_catalog_unknown_title")
      : t("access_effective_unknown_title");

    if (explainUnknown) {
      return (
        <div className="flex items-start gap-2">
          <span className="font-mono text-[12.5px] font-semibold text-muted-foreground/70">?</span>
          <p className="text-[11.5px] leading-snug text-muted-foreground">{reason}</p>
        </div>
      );
    }

    return (
      <span className="font-mono text-[12.5px] font-semibold text-muted-foreground/70" title={reason}>
        ?
      </span>
    );
  }

  if (noRoles || !summary) {
    return <span className="font-mono text-[11px] text-muted-foreground/60">—</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <span
        className="font-mono text-[12.5px] font-semibold tabular-nums text-primary"
        title={t("access_effective_granted_title")}
      >
        {summary.allowed}
        <span className="font-normal text-muted-foreground/60">/{summary.total}</span>
      </span>
      {summary.denied > 0 && (
        <span
          className="inline-flex items-center font-mono text-[11px] font-semibold text-destructive"
          title={t("access_effective_denied_title")}
        >
          <X size={9} />
          {summary.denied}
        </span>
      )}
    </div>
  );
}
